import { Component, ContentChild, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { onConfirmDialog } from '../../../../../core/helpers/helpers.function';
import { IAvailableTypes, IConstantItem } from '../../../../../core/models/available-types.interface';
import { ICountry } from '../../../../../core/models/country.interface';
import { PageParams } from '../../../../../core/models/page-params.model';
import { NotificationService } from '../../../../../core/services/notification.service';
import { UserService } from '../../../../../core/services/user.service';
import { ConfirmPopupDialog } from '../../../../../shared/popups/confirm-popup/confirm-popup.component';
import {
    IActivityUnitOfMeasure,
    ITypeActivity,
    ITypeActivityPrice
} from '../../../models/shipment-price.interface';
import { ShipmentPriceService } from '../../../services/shipment-price.service';

@Component({
    selector: 'eci-table-type-activity-prices',
    templateUrl: './table-type-activity-prices.component.html',
    styleUrls: [ './table-type-activity-prices.component.scss' ],
})

export class TableTypeActivityPricesComponent implements OnInit {
    @Input() public dataSource: MatTableDataSource<ITypeActivityPrice>;
    @Input() public displayedColumns: string[];
    @Input() shipmentPriceItemId: number;
    @Input() theCountriesList: ICountry[];
    @Input() isCreateBtn: boolean = true;
    @Input() pageParams: PageParams = new PageParams();
    @Input() public theTypeActivityList: ITypeActivity[] = [];
    @Input() public theSubTypeActivityList: ITypeActivity[] = [];
    @Input() public theActivityUnitList: IActivityUnitOfMeasure[] = [];
    @Input() weightUnits: IConstantItem[] = [
        {id: 0, name: 'g'},
        {id: 1, name: 'kg'},
        {id: 2, name: 't'},
    ]; // Default list. Can be changed from parent component.
    @Input() dimensionsUnits: IConstantItem[] = [
        {id: 0, name: 'mm'},
        {id: 1, name: 'cm'},
        {id: 2, name: 'm'},
        {id: 3, name: 'dm'},
        {id: 4, name: 'km'},
    ]; // Default list. Can be changed from parent component.
    @Input() volumeUnits: IConstantItem[] = [
        {id: 1, name: 'liters'},
        {id: 2, name: 'mm3'},
        {id: 3, name: 'cm3'},
        {id: 4, name: 'm3'},
    ]; // Default list. Can be changed from parent component.

    @Input() quantityUnits: IConstantItem[] = [
        {id: 0, name: 'pcs'},
        {id: 1, name: 'box'},
        {id: 2, name: 'pallet'},
    ]; // Default list. Can be changed from parent component.
    @Output() pageChange: EventEmitter<PageParams> = new EventEmitter<PageParams>(null);
    @Output() onNavigate: EventEmitter<any> = new EventEmitter<any>(null);
    @Output() sortRows: EventEmitter<Sort> = new EventEmitter<Sort>(null);
    // Todo: Should be generic type
    @Output() public onEdit: EventEmitter<any> = new EventEmitter<any>(null);
    @Output() public onCancel: EventEmitter<any> = new EventEmitter<any>(null);
    @Output() public onSave: EventEmitter<any> = new EventEmitter<any>(null);
    @Output() public onDelete: EventEmitter<any> = new EventEmitter<any>(null);

    @Output() public onNewRow: EventEmitter<any> = new EventEmitter<any>(null);
    @ViewChild(MatSort) public sort: MatSort;
    @ContentChild('controlTemplate') public controlTemplate: TemplateRef<any>;

    @ContentChild('paginatorTemplate') public paginatorTemplate: TemplateRef<any>;
    public theSubTypeActivityByParentIdList: ITypeActivity[] = [];
    public isEditMode: boolean | number = false;
    public addNewRowDataBtn: boolean = false;
    public form: FormGroup;
    public availableTypes: IAvailableTypes;

    public countriesList: ICountry[] = [];
    public showLoader: boolean = false;
    public typeNameSearch: FormControl = new FormControl('');
    public subTypeNameSearch: FormControl = new FormControl('');
    public activityUnitSearch: FormControl = new FormControl('');
    public searchCountry: FormControl = new FormControl('');

    constructor(
        public userSvc: UserService,
        private dialog: MatDialog,
        private shipmentPriceService: ShipmentPriceService,
        private notify: NotificationService,
    ) {
    }

    ngOnInit(): void {
        this.countriesList = this.theCountriesList;
        this.form = this.initializeForm();
    }

    private initializeForm(data?: ITypeActivityPrice): FormGroup {
        console.log('initializeForm_data', data);
        return new FormGroup({
            shipmentPriceItemId: new FormControl({disabled: false, value: data?.shipmentPriceItemId || this?.shipmentPriceItemId},
                [ Validators.required ]),
            typeName: new FormControl({
                    disabled: false,
                    value: data?.typeName || data?.parentTypeActivity?.name || data?.typeActivity?.parent?.name || undefined
                },
                [ Validators.required ]),
            subTypeName: new FormControl({disabled: false, value: data?.subTypeName || data?.typeActivity?.name || 'None'},
                [ /*Validators.required*/ ]),
            description: new FormControl({disabled: false, value: data?.description || data?.typeActivity?.description || undefined},
                [ /*Validators.required*/ ]),
            activityUnitName: new FormControl({disabled: false, value: data?.activityUnitName || data?.activityUnit?.name || undefined},
                [ Validators.required ]),
            costRates: new FormControl({disabled: false, value: data?.costRates || undefined},
                [ /*Validators.required,*/ Validators.min(0.01) ]),
            margin: new FormControl({disabled: false, value: data?.margin || undefined},
                [ Validators.required, Validators.min(0.01), Validators.max(100) ]),
            sellPrice: new FormControl({disabled: false, value: data?.sellPrice || undefined},
                [ Validators.required, Validators.min(0.01) ]),
            currency: new FormControl({disabled: false, value: data?.currency || undefined},
                [ Validators.required, Validators.minLength(3), Validators.maxLength(3) ]),
            maxLength: new FormControl({disabled: false, value: data?.maxLength || data?.restriction?.maxLength || undefined},
                [ Validators.required, Validators.min(0.01) ]),
            maxWidth: new FormControl({disabled: false, value: data?.maxWidth || data?.restriction?.maxWidth || undefined},
                [ Validators.required, Validators.min(0.01) ]),
            maxHeight: new FormControl({disabled: false, value: data?.maxHeight || data?.restriction?.maxHeight || undefined},
                [ Validators.required, Validators.min(0.01) ]),
            dimensionsUnitsOfMeasure: new FormControl({
                    disabled: false,
                    value: data?.dimensionsUnitsOfMeasure || data?.restriction?.dimensionsUnitsOfMeasure || 'cm'
                },
                [ Validators.required ]),
            maxWeight: new FormControl({disabled: false, value: data?.maxWeight || data?.restriction?.maxWeight || undefined},
                [ Validators.required, Validators.min(0.01) ]),
            weightUnitsOfMeasure: new FormControl({
                    disabled: false,
                    value: data?.weightUnitsOfMeasure || data?.restriction?.weightUnitsOfMeasure || 'kg'
                },
                [ Validators.required ]),
            maxVolume: new FormControl({disabled: false, value: data?.maxVolume || data?.restriction?.maxVolume || undefined},
                [ Validators.required, Validators.min(0.01) ]),
            volumeUnitsOfMeasure: new FormControl({
                    disabled: false,
                    value: data?.volumeUnitsOfMeasure || data?.restriction?.volumeUnitsOfMeasure || 'liters'
                },
                [ Validators.required ]),
        });
    }

    public sortData(e: Sort): void {
        this.sortRows.emit(e);
    }

    public convertToPrice(value: any): any {
        if (value === undefined || value === null) {
            return '';
        }
        return this.convertToMoney(value);
    }

    public convertToPercentage(value: any): any {
        if (value === undefined || value === null) {
            return '';
        }
        return this.convertToMoney(value) + '%';
    }

    private convertToMoney(value: any): any {
        return parseFloat(value || 0).toFixed(2);
    }

    public save(event: MouseEvent, element, index) {
        console.log('save_element', element);
        event.stopPropagation();
        if (this.form.invalid) {
            return;
        }
        this.onSave.emit({
            form: this.form.value, element, onSuccess: () => {
                this.isEditMode = false;
                this.addNewRowDataBtn = false;
            }, index
        });
    }

    public close(event: MouseEvent, element, index) {
        event.stopPropagation();
        console.log('close_element', element);
        const onConfirm = () => {
            if (!element.id) {
                this.removeData(index);
            }
            this.isEditMode = false;
            this.addNewRowDataBtn = false;
        };
        const dialogRef = this.dialog.open(ConfirmPopupDialog);
        dialogRef.componentInstance.message = `Are you sure you want to cancel Edit action.`;
        dialogRef.componentInstance.rightBtnText = 'Yes';
        dialogRef.componentInstance.leftBtnText = 'No';
        dialogRef.afterClosed().subscribe(value => {
            if (!value) {
                return;
            }
            onConfirm();
        });
    }

    public delete(event: MouseEvent, id, index) {
        event.stopPropagation();
        console.log('del_element', id, index);
        const onConfirm = () => {
            this.onDelete.emit({id, index});
            this.isEditMode = false;
            this.addNewRowDataBtn = false;
        };
        const dialogRef = this.dialog.open(ConfirmPopupDialog);
        onConfirmDialog(dialogRef, onConfirm, 'entity');
    }

    public edit(event: MouseEvent, element, index) {
        event.stopPropagation();
        const currentData: any = {
            shipmentPriceItemId: element?.shipmentPriceItemId || this.shipmentPriceItemId,
            typeName: this.getCurrentIdByNames(element?.typeName || element.parentTypeActivity?.id || element.typeActivity?.parent?.id, this.theTypeActivityList),
            subTypeName: this.getCurrentIdByNames(element?.subTypeName || element?.typeActivity?.id, this.theSubTypeActivityList),
            typeId: element?.typeId || element?.typeActivity?.id,
            activityUnitName: this.getCurrentIdByNames(element?.activityUnitName || element?.activityUnit?.id, this.theActivityUnitList),
            description: element?.description || element?.typeActivity?.description,
            costRates: element?.costRates,
            margin: element?.margin,
            sellPrice: element?.sellPrice,
            currency: element?.currency,
            maxWidth: element?.maxWidth || element?.restriction?.maxWidth,
            maxHeight: element?.maxHeight || element?.restriction?.maxHeight,
            maxLength: element?.maxLength || element?.restriction?.maxLength,
            dimensionsUnitsOfMeasure: this.getCurrentIdByNames(element?.dimensionsUnitsOfMeasure || element?.restriction?.dimensionsUnitsOfMeasure, this.dimensionsUnits),
            maxWeight: element?.maxWeight || element?.restriction?.maxWeight,
            weightUnitsOfMeasure: this.getCurrentIdByNames(element?.weightUnitsOfMeasure || element?.restriction?.weightUnitsOfMeasure, this.weightUnits),
            maxVolume: element?.maxVolume || element?.restriction?.maxVolume,
            volumeUnitsOfMeasure: this.getCurrentIdByNames(element?.volumeUnitsOfMeasure || element?.restriction?.volumeUnitsOfMeasure, this.volumeUnits),
        };
        this.form = this.initializeForm(currentData);
        this.form.patchValue(currentData);
        this.isEditMode = element.id;
        this.addNewRowDataBtn = true;
    }

    public addNewRow(): void {
        this.addNewRowDataBtn = true;
        const newData: ITypeActivityPrice = {
            typeId: undefined,
            typeName: undefined,
            subTypeName: undefined,
            description: undefined,
            activityUnitName: undefined,
            costRates: undefined,
            margin: undefined,
            sellPrice: undefined,
            currency: undefined,// 'EUR'
            maxWidth: undefined,
            maxHeight: undefined,
            maxLength: undefined,
            dimensionsUnitsOfMeasure: 1,
            maxWeight: undefined,
            weightUnitsOfMeasure: 1,
            maxVolume: undefined,
            volumeUnitsOfMeasure: 1,
            shipmentPriceItemId: this.shipmentPriceItemId || this.shipmentPriceItemId
        };
        // console.log('addNewRo_element', newData);
        this.isEditMode = undefined;
        this.form = this.initializeForm(newData);
        this.onNewRow.emit(newData);
    }

    public get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    getCorrectCountryByDestination(destination: any) {
        const selectedCountry = this.getCountryDataByDestination(destination);
        return (selectedCountry ? `${selectedCountry.name}(${selectedCountry.code})` : undefined);
    }

    protected getCountryDataByDestination(destination: any) {
        return (this.theCountriesList?.find((country) => (+country.id === +destination ||
                country.code === destination || country.name === destination || country.codeFull === destination))
            || undefined);
    }

    public compareCountries = (o1: any, o2: any) => {
        if (!o1 || !o2) {
            return false;
        }
        return (+o1 === +o2);
    };

    protected getCurrentIdByNames(name: string, list: any[]) {
        if (!name || !list) {
            return undefined;
        }
        if (!isNaN(+name)) {
            return name;
        }
        const selected = list?.find((item) => item.name === name);
        return (selected ? selected.id : undefined);
    }

    private removeData(index: number) {
        this.onCancel.emit(index);
    }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    public validateCurrency(event: KeyboardEvent): boolean {
        return !!event.key.match(/[A-Z]/);
    }

    public createTypeName(typeName: string): void {
        if (!typeName) {
            return;
        }
        this.showLoader = true;
        this.shipmentPriceService.create<ITypeActivity>('type-activities',
            {name: typeName}).subscribe((data) => {
            if (!data.id) {
                return;
            }
            this.notify.showSuccessMessage(`Created successfully`);
            this.theTypeActivityList.push(data);
            this.showLoader = false;
        }, (err) => {
            this.notify.showError(err.error.message);
            this.showLoader = false;
        });
    }

    createSubTypeName(subTypeName: string) {
        // const parentId = +this.form.get('typeName')?.value; // Changes for delete relation with parent fix for ##6446
        if (!subTypeName /*|| !parentId*/) {
            // this.notify.showWarn(`Select first Type, please.`);
            return;
        }
        this.showLoader = true;
        this.shipmentPriceService.create<ITypeActivity>('type-activities',
            {name: subTypeName, isSubType: true, /*parentId: parentId*/}).subscribe((data) => {
            if (!data.id) {
                return;
            }
            this.notify.showSuccessMessage(`Created successfully`);
            this.theSubTypeActivityList.push(data);
            this.showLoader = false;
        }, (err) => {
            this.notify.showError(err.error.message);
            this.showLoader = false;
        });
    }

    createActivityUnit(activityUnitName: string) {
        if (!activityUnitName) {
            return;
        }
        this.showLoader = true;
        this.shipmentPriceService.create<IActivityUnitOfMeasure>('activity-unit-of-measures',
            {name: activityUnitName}).subscribe((data) => {
            if (!data.id) {
                return;
            }
            this.notify.showSuccessMessage(`Created successfully`);
            this.theActivityUnitList.push(data);
            this.showLoader = false;
        }, (err) => {
            this.notify.showError(err.error.message);
            this.showLoader = false;
        });
    }

    // getSubTypeActivityListByParentId(parentId: number) {
    //     this.shipmentPriceService.get(`type-activities/${parentId}/sub-types`, undefined, undefined, undefined,
    //         {isSubType: true, parentId}).subscribe((data: any) => {
    //         this.theSubTypeActivityByParentIdList = data?.items;
    //     });
    // }
}
